import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { faXmark } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { exhaustiveGuard } from '@soundxyz/utils';

import { PlaylistCard } from '@components/Playlists/basics/PlaylistCard';
import type { SavedEntity } from '@components/SearchBar/SearchHistory/schema';
import { removeSearchHistory } from '@components/SearchBar/SearchHistory/useSearchHistoryStore';
import { SearchSongCard } from '@components/SongCards/SearchSongCard';
import { ArtistUserCard } from '@components/basics/UserCard/ArtistUserCard';
import { CollectorUserCard } from '@components/basics/UserCard/CollectorUserCard';
import { getFragment, useQuery } from '@graphql/reactQuery';
import {
  SearchHistoryArtistDocument,
  SearchHistoryCollectorDocument,
  SearchHistoryPlaylistDocument,
  SearchHistoryReleaseDocument,
  SearchResultShelfFragmentDoc,
} from '@graphql/reactQuery';
import { SearchArtistRow } from './Rows/SearchArtistRow';
import { SearchCollectorRow } from './Rows/SearchCollectorRow';
import { SearchPlaylistRow } from './Rows/SearchPlaylistRow';
import { SearchSongRow } from './Rows/SearchSongRow';

function RemoveSearchHistory({ id }: { id: string }) {
  return (
    <button
      onClick={() => {
        removeSearchHistory({
          id,
        });
      }}
      className={clsx(
        'max-w-7 right-2 top-2 max-h-7 rounded-full border-none bg-none md:absolute',
        'flex duration-100 group-hover:flex md:hidden',
        'group-focus:right-[7px] group-focus:top-[7px]',
      )}
    >
      <FontAwesomeIcon
        icon={faXmark}
        size="xs"
        className="aspect-square rounded-full p-2 text-black backdrop-blur-sm transition-colors hover:bg-base700/70 md:bg-base700/70 md:text-white md:hover:bg-black"
      />
    </button>
  );
}

export function SearchHistoryCard({ entity }: { entity: SavedEntity }) {
  switch (entity.type) {
    case 'Release': {
      return <SearchHistoryRelease entity={entity} />;
    }
    case 'Artist': {
      return <SearchHistoryArtist entity={entity} />;
    }
    case 'Playlist': {
      return <SearchHistoryPlaylist entity={entity} />;
    }
    case 'Collector': {
      return <SearchHistoryCollector entity={entity} />;
    }
    default: {
      exhaustiveGuard(entity.type);
    }
  }
}

function SearchHistoryRelease({ entity: { id } }: { entity: SavedEntity }) {
  const { data } = useQuery(SearchHistoryReleaseDocument, {
    variables: {
      id,
    },

    onError(error) {
      // TODO: log error here https://linear.app/sound/issue/ENG-7138/add-error-reporting
      // eslint-disable-next-line no-console
      console.error(error);

      removeSearchHistory({
        id,
      });
    },
    staleTime: 0,
  });

  // TODO: Loading state https://linear.app/sound/issue/ENG-2044/independent-loading-state-of-search-history-rows
  if (!data?.data.release) return null;

  return (
    <div className="group relative flex items-center justify-between md:block">
      <div className="hidden md:block">
        <SearchSongCard data={data.data.release} getNextTrackIds={undefined} />
      </div>
      <div className="w-full min-w-0 md:hidden">
        <SearchSongRow data={data.data.release} />
      </div>
      <RemoveSearchHistory id={id} />
    </div>
  );
}

function SearchHistoryArtist({ entity: { id } }: { entity: SavedEntity }) {
  const { data } = useQuery(SearchHistoryArtistDocument, {
    variables: {
      id,
    },
    staleTime: Infinity,
    onError(error) {
      // TODO: log error here https://linear.app/sound/issue/ENG-7138/add-error-reporting
      // eslint-disable-next-line no-console
      console.error(error);

      removeSearchHistory({
        id,
      });
    },
  });

  // TODO: Loading state https://linear.app/sound/issue/ENG-2044/independent-loading-state-of-search-history-rows
  if (!data?.data.artist) return null;

  return (
    <div className="group relative flex items-center justify-between md:block">
      <div className="hidden md:block">
        <ArtistUserCard artist={data.data.artist} />
      </div>
      <div className="w-full min-w-0 md:hidden">
        <SearchArtistRow data={data.data.artist} />
      </div>
      <RemoveSearchHistory id={id} />
    </div>
  );
}

function SearchHistoryPlaylist({ entity: { id } }: { entity: SavedEntity }) {
  const { data } = useQuery(SearchHistoryPlaylistDocument, {
    variables: {
      id,
    },
    staleTime: Infinity,
    onError(error) {
      // TODO: log error here https://linear.app/sound/issue/ENG-7138/add-error-reporting
      // eslint-disable-next-line no-console
      console.error(error);

      removeSearchHistory({
        id,
      });
    },
  });

  const playlist = getFragment(SearchResultShelfFragmentDoc, data?.data.shelf);

  // TODO: Loading state https://linear.app/sound/issue/ENG-6907/improve-cards-loading-states
  if (!playlist) return null;

  return (
    <div className="group relative flex items-center justify-between md:block">
      <div className="hidden md:block">
        <PlaylistCard data={playlist} showMetadata={false} />
      </div>
      <div className="w-full min-w-0 md:hidden">
        <SearchPlaylistRow data={playlist} />
      </div>
      <RemoveSearchHistory id={id} />
    </div>
  );
}

function SearchHistoryCollector({ entity: { id } }: { entity: SavedEntity }) {
  const { data } = useQuery(SearchHistoryCollectorDocument, {
    variables: {
      id,
    },
    staleTime: Infinity,
    onError(error) {
      // TODO: log error here https://linear.app/sound/issue/ENG-7138/add-error-reporting
      // eslint-disable-next-line no-console
      console.error(error);

      removeSearchHistory({
        id,
      });
    },
  });

  // TODO: Loading state https://linear.app/sound/issue/ENG-2044/independent-loading-state-of-search-history-rows
  if (!data?.data.user) return null;

  return (
    <div className="group relative flex items-center justify-between md:block">
      <div className="hidden md:block">
        <CollectorUserCard collector={data.data.user} />
      </div>
      <div className="w-full min-w-0 md:hidden">
        <SearchCollectorRow data={data.data.user} />
      </div>
      <RemoveSearchHistory id={id} />
    </div>
  );
}
